/* THE EXAMPLE WORKSPACE — a coach with no clients can look around a whole made-up practice (his choice A, 2026-09-28).
 *
 * 🔴 THE ONE PROMISE: NOTHING THE COACH DOES HERE REACHES THE DATABASE. It is kept by the NETWORK, not by each screen:
 * (Week editor stage 1, 2026-10-19: ONE write — the client Week's `write_week_trees` — is ANSWERED in this tab, applied to
 * the made-up world in memory, so the editor can be tried here; it is still never sent, and a reload restores the example.)
 * while the example is on, every request to this project goes through `exampleVerdict`, which lets the SIGN-IN service
 * through (a session must keep refreshing), answers table READS from the made-up world, and REFUSES everything else —
 * every insert, update, delete, write RPC, storage call and edge function — before a byte leaves the browser. A screen
 * that tries to save shows its own error with the refusal's sentence, and the banner says why. The same guard sits on
 * `XMLHttpRequest`, because the video upload (tus) does not use `fetch`.
 *
 * ⚠️ WHAT IT IS NOT: fake accounts, fake `coach_clients` rows, anything in the live sign-in system, or a way past the
 * only-the-client-accepts guard. The example's people exist in this tab's memory and nowhere else.
 *
 * ⚠️ ENTERING AND LEAVING RELOAD THE PAGE, deliberately: every screen reads its data once, so a switch without a reload
 * would leave real rows on screen beside made-up ones. The flag lives in `sessionStorage` — one tab, gone when it closes.
 */

import { SUPABASE_URL, supabase } from "../supabase";
import { href } from "../route";
import { webAccess } from "../signUp";
import { answer, answerRpc, type Fixture } from "./rest";
import { applyDayOrder, isDayOrderWrite } from "./weekLibrary";
import { exampleWorld } from "./world";

export const EXAMPLE_FLAG = "ht-example";
/** The event a refused write raises, so the banner can say why nothing saved. */
export const EXAMPLE_REFUSED_EVENT = "ht:example-refused";
/** Where that event is raised — its own target, so the page and the tests hear it alike. */
export const exampleEvents = new EventTarget();
export const EXAMPLE_REFUSAL = "This is the example workspace — nothing here can be changed or saved.";

const PROJECT_REF = new URL(SUPABASE_URL).hostname.split(".")[0] ?? "";

/** The RPCs that only READ, answered from the example world. Every other RPC is a write and is refused. */
const READ_RPCS = new Set(["assigned_workout_tree", "peek_coach_invitation", "reviewer_links"]);
/** Week editor stage 1 (2026-10-19) — the ONE write the example ANSWERS instead of refusing: the client Week's editor, applied
 *  to this tab's made-up world in memory (`weekTrees.ts`), so a coach can try editing a week here. Still never sent: the
 *  answer is made in this tab like every read's, and a reload brings the example back as it was. */
export const EXAMPLE_LOCAL_WRITES = new Set(["write_week_trees", "assign_coach_workout", "save_week_as_program"]);
/* Week editor stage 2 (2026-10-19): a library workout dropped on a day (`assign_coach_workout`) and a week saved as a program
   (`save_week_as_program`) are answered the same way (`weekLibrary.ts`); so is the day's ORDER — a `PATCH coach_assignments`
   carrying `day_position` alone (`isDayOrderWrite`, decided on the BODY like `isAutomaticWrite`). Every other write: refused. */

export type ExampleVerdict = "pass" | "answer" | "refuse" | "ignore";

/** The writes a page makes on its OWN, with nobody pressing anything — today one: opening a thread stamps `read_at`.
 *  In the example they are IGNORED: answered as done, nothing written, no alarm — refusing them would flash "nothing can
 *  be saved" at a coach who only opened a conversation. Still never sent: `ignore` answers locally like `refuse`. */
export function isAutomaticWrite(method: string, path: string, body: unknown): boolean {
  if (method.toUpperCase() !== "PATCH" || path !== "/rest/v1/coach_messages" || typeof body !== "string") return false;
  try {
    const parsed: unknown = JSON.parse(body);
    return parsed !== null && typeof parsed === "object" && Object.keys(parsed).join(",") === "read_at";
  } catch { return false; }
}

/** What happens to one request while the example is on. PURE — the whole promise, in one testable function. */
export function exampleVerdict(method: string, href: string): ExampleVerdict {
  let url: URL;
  try { url = new URL(href, "http://relative.invalid"); } catch { return "pass"; }
  const host = url.hostname;
  const ours = host.endsWith(".supabase.co") && host.split(".")[0] === PROJECT_REF;
  if (!ours) return "pass";
  const path = url.pathname;
  if (path.startsWith("/auth/v1/")) return "pass";
  const verb = method.toUpperCase();
  if (path.startsWith("/rest/v1/rpc/")) {
    const fn = path.slice("/rest/v1/rpc/".length);
    return READ_RPCS.has(fn) || EXAMPLE_LOCAL_WRITES.has(fn) ? "answer" : "refuse";
  }
  if (path.startsWith("/rest/v1/")) return verb === "GET" || verb === "HEAD" ? "answer" : "refuse";
  // Storage READS are answered here as "nothing there" (the example holds no media): a listing, a signed link, a
  // download. Storage's list and sign are POSTs, which is why the method alone cannot decide. Everything else in
  // storage — an upload, a replace, a delete — is a write and is refused.
  if (path.startsWith("/storage/v1/object/list/") || path.startsWith("/storage/v1/object/sign/")) return "answer";
  if (path.startsWith("/storage/v1/object/") && (verb === "GET" || verb === "HEAD")) return "answer";
  return "refuse";
}

const flagStore = (): Storage | null => { try { return globalThis.sessionStorage ?? null; } catch { return null; } };

/** Whether this tab asked for the example. */
export function exampleRequested(): boolean {
  return flagStore()?.getItem(EXAMPLE_FLAG) === "1";
}

let active = false;
/** Whether the example is ON in this page — the banner and the entry links read this. */
export function exampleActive(): boolean { return active; }

/** Enter the example: set the tab's flag and reload at the Dashboard. */
export function enterExample(): void {
  flagStore()?.setItem(EXAMPLE_FLAG, "1");
  window.location.hash = href({ name: "dashboard" });
  window.location.reload();
}

/** Leave the example: clear the flag and reload at the Dashboard, on the coach's real data. */
export function leaveExample(): void {
  flagStore()?.removeItem(EXAMPLE_FLAG);
  window.location.hash = href({ name: "dashboard" });
  window.location.reload();
}

function refusal(): Response {
  exampleEvents.dispatchEvent(new Event(EXAMPLE_REFUSED_EVENT));
  return new Response(JSON.stringify({ code: "EXAMPLE", message: EXAMPLE_REFUSAL, details: null, hint: null }), {
    status: 403, headers: { "content-type": "application/json" },
  });
}

/** Answer one request under the example, given its verdict. Exported for tests. */
export function exampleResponse(fx: Fixture, verdict: ExampleVerdict, href: string, headers: Headers, init?: RequestInit): Response | null {
  if (verdict === "pass") return null;
  if (verdict === "ignore") return new Response("[]", { status: 200, headers: { "content-type": "application/json" } });
  if (verdict === "refuse") {
    // Said in the console with the request, so a refusal nobody pressed a button for can be found.
    console.info(`[example] refused ${(init?.method ?? "GET").toUpperCase()} ${new URL(href).pathname}`);
    return refusal();
  }
  const url = new URL(href);
  if (url.pathname.startsWith("/storage/v1/object/list/")) return new Response("[]", { status: 200, headers: { "content-type": "application/json" } });
  if (url.pathname.startsWith("/storage/v1/")) {
    return new Response(JSON.stringify({ statusCode: "404", error: "not_found", message: "Object not found" }), { status: 400, headers: { "content-type": "application/json" } });
  }
  if (url.pathname.startsWith("/rest/v1/rpc/")) return answerRpc(url, headers, fx, init);
  return answer(url, headers, fx);
}

/**
 * Turn the example on for this page, before the app mounts. Returns false (and clears the flag) when it cannot — no
 * session, or not a coach — so a stale flag can never strand somebody in it.
 */
export async function installExample(): Promise<boolean> {
  if (!exampleRequested()) return false;
  const { data } = await supabase.auth.getSession();
  const uid = data.session?.user.id;
  if (uid === undefined) { flagStore()?.removeItem(EXAMPLE_FLAG); return false; }
  // The one real read: the coach's own name, for their own card. Made BEFORE the guard is installed.
  const { data: me } = await supabase.from("profiles")
    .select("id, first_name, last_name, avatar_url, account_role").eq("id", uid).maybeSingle();
  if (me === null || webAccess(me.account_role) !== "coach") { flagStore()?.removeItem(EXAMPLE_FLAG); return false; }
  const world = exampleWorld({ id: uid, first_name: me.first_name, last_name: me.last_name, avatar_url: me.avatar_url }, new Date());
  /* W126 — DEV ONLY: `?ht-watch` puts a two-week watch on the example (`dev/exampleWatch.ts`), so the Clients row and the
     client page's `Watch` block can be rendered. `import.meta.env.DEV` is `false` in a build, so the branch and its chunk go. */
  const watched = import.meta.env.DEV && new URLSearchParams(window.location.search).has("ht-watch")
    ? (await import("../../dev/exampleWatch")).withWatch(world, new Date())
    : world;
  // W127 — DEV ONLY: `?ht-band` gives the example's completed sets an OLD band label (`dev/exampleWatch.ts`).
  const fx = import.meta.env.DEV && new URLSearchParams(window.location.search).has("ht-band")
    ? (await import("../../dev/exampleWatch")).withOldBandLabels(watched)
    : watched;

  const real = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const href = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    let verdict = exampleVerdict(method, href);
    if (verdict === "refuse" && isAutomaticWrite(method, new URL(href).pathname, init?.body)) verdict = "ignore";
    if (verdict === "refuse" && isDayOrderWrite(method, new URL(href).pathname, init?.body)) {
      applyDayOrder(fx, new URL(href), String(init?.body));
      return new Response(null, { status: 204 });
    }
    return exampleResponse(fx, verdict, href, headers, init) ?? real(input, init);
  };

  // The video upload speaks XMLHttpRequest (tus), so the same verdict guards it: a refused request never opens.
  const open = XMLHttpRequest.prototype.open;
  const send = XMLHttpRequest.prototype.send;
  const refused = new WeakSet<XMLHttpRequest>();
  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    if (exampleVerdict(method, String(url)) !== "pass") refused.add(this);
    return (open as (...a: unknown[]) => void).call(this, method, url, ...rest);
  } as typeof XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    if (refused.has(this)) {
      exampleEvents.dispatchEvent(new Event(EXAMPLE_REFUSED_EVENT));
      setTimeout(() => { this.dispatchEvent(new ProgressEvent("error")); }, 0);
      return;
    }
    send.call(this, body);
  };

  active = true;
  return true;
}
